export type InternationalPhoneLocale = {
  countryCodes: string[];
  placeholders: Record<string, string>;
};

export type InternationalPhoneLocaleDelta = {
  omit?: {
    countryCodes?: string[];
  };
  extend?: {
    countryCodes?: string[];
    placeholders?: Partial<Record<string, string>>;
  };
};

const normalizeCode = (code: string) => code.toLowerCase();

function dedupeCountryCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  return codes.filter((c) => {
    const k = normalizeCode(c);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function mergeInternationalPhoneLocale(
  base: InternationalPhoneLocale,
  delta: InternationalPhoneLocaleDelta = {}
): InternationalPhoneLocale {
  const toOmit = delta.omit?.countryCodes ?? [];
  const omitSet = new Set(toOmit.map(normalizeCode));

  const countryCodes = dedupeCountryCodes([
    ...base.countryCodes.filter((c) => !omitSet.has(normalizeCode(c))),
    ...(delta.extend?.countryCodes ?? []),
  ]);

  const mergedPlaceholders = { ...base.placeholders };
  for (const code of toOmit) {
    delete mergedPlaceholders[normalizeCode(code)];
  }
  Object.assign(mergedPlaceholders, delta.extend?.placeholders ?? {});

  const placeholders: Record<string, string> = {};
  for (const code of countryCodes) {
    const key = normalizeCode(code);
    const val = mergedPlaceholders[key];
    if (val !== undefined) placeholders[key] = val;
  }

  return { countryCodes, placeholders };
}

/** Shared country list and placeholders used by search and FraudScan variants. */
export const INTERNATIONAL_PHONE_LOCALE_BASE: InternationalPhoneLocale = {
  countryCodes: [
    'in',
    'ca',
    'mx',
    'pe',
    'id',
    'th',
    'ar',
    'ph',
    'co',
    'tr',
    'cl',
    'il',
    'us',
    'uy',
    'py',
    'my',
    'za',
    'sg',
    'kz',
    'do',
    'ec',
    'ge',
    'na',
    'kw',
    'mv',
  ],
  placeholders: {
    in: '74104 10123',
    ca: '506-234-5678',
    mx: '200 123 4567',
    pe: '123 456 789',
    id: '123 4567 8910',
    th: '12 345 6789',
    ar: '11 2345-6789',
    ph: '123 456 7890',
    co: '601 234 5678',
    tr: '212 345 6789',
    cl: '2 2123 4567',
    il: '123-4567',
    us: '201-555-0123',
    uy: '2901 2345',
    py: '021 234 567',
    my: '03-1234 5678',
    za: '021 123 4567',
    sg: '6123 4567',
    kz: '727 123 4567',
    do: '809 123 4567',
    ec: '02 345 6789',
    ge: '012345678',
    na: '012345678',
    kw: '01234567',
    mv: '0123456',
  },
};

/**
 * - `omit.countryCodes` — ISO codes to remove from the base list (and drop their placeholders).
 * - `extend.countryCodes` — extra ISO codes appended after the base (minus omitted).
 * - `extend.placeholders` — placeholders for extended codes, or overrides for codes still in the list.
 */
export const INTERNATIONAL_PHONE_SEARCH_DELTA: InternationalPhoneLocaleDelta =
  {};

export const INTERNATIONAL_PHONE_FRAUD_SCAN_DELTA: InternationalPhoneLocaleDelta =
  {
    extend: {
      countryCodes: ['br'],
      placeholders: { br: '11 91234-5678' },
    },
  };

export const internationalPhoneSearchLocale = mergeInternationalPhoneLocale(
  INTERNATIONAL_PHONE_LOCALE_BASE,
  INTERNATIONAL_PHONE_SEARCH_DELTA
);

export const internationalPhoneFraudSearchLocale =
  mergeInternationalPhoneLocale(
    INTERNATIONAL_PHONE_LOCALE_BASE,
    INTERNATIONAL_PHONE_FRAUD_SCAN_DELTA
  );
